import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Invoices } from './queryOptions';
import { ActionButton, TableCell } from '@maia/ui';
import { IconDownloadAlt } from '@maia/icons';
import { useCallback } from 'react';
import { service } from '@maia/attachments';
import { useQuery } from '@tanstack/react-query';
import { downloadEncodedDocument } from '@maia/utils';

type Invoice = Invoices['response'][number];

const columns: ColumnDef<Invoice>[] = [
  {
    accessorKey: 'reference',
    header: 'Factuurnummer',
    cell: ({ row }) => <TableCell.Text text={row.original.reference} />,
    enableSorting: false,
  },
  {
    accessorKey: 'createdAt',
    header: 'Datum',
    cell: ({ row }) => <TableCell.Date date={row.original.date} />,
    enableSorting: false,
  },
  {
    accessorKey: 'price',
    header: 'Bedrag',
    cell: ({ row }) => <TableCell.Price price={row.original.amount} />,
    enableSorting: false,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <TableCell.Tag
        {...row.original.status}
        tooltip={row.original.paymentRemark ?? undefined}
      />
    ),
    enableSorting: false,
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const { refetch } = useQuery({
        queryFn: () => service.find(row.original.guid),
        queryKey: [row.original.guid],
        enabled: false,
      });

      const handleDownload = useCallback(async () => {
        const documentData = await refetch().then((d) => d.data);
        if (documentData) {
          return downloadEncodedDocument(documentData);
        }
      }, []);

      return (
        <div className="flex gap-x-12 items-center">
          <ActionButton
            icon={IconDownloadAlt}
            size="small"
            onPress={handleDownload}
          />
        </div>
      );
    },
  },
];

export function useInvoiceTable(data?: Invoices) {
  return useReactTable({
    columns,
    data: data?.response ?? [],
    rowCount: data?.response.length,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: data?.pagination.totalPages,
  });
}
